import { DeleteOutlined, EyeOutlined } from "@ant-design/icons";
import { ParentRegex } from "@dto/customGroup.dto";
import { Button, Form, Input } from "antd";
import { useContext } from "react";
import { XmSelect } from "src/component/select/XmSelect";
import { GLOB } from "src/util/Glob";
import { AdditionalApiPropsContext, RuleContext, invalidHwProps } from "../XmRule";
import { isPatternValid, validatePattern } from "../children/RegexList";
import { AllItemsContext } from "../comp/AllItems";
import { usePreviewModal } from "../comp/preview/Preview";
import { Option } from "../hook/useParentTypes";
import AddButton from "./AddButton";

function RegexList({ parentTypes }: { parentTypes: Option[] }) {
  const { cls, subsystem: subsys, hw_type } = useContext(AllItemsContext);
  const { preview, additionalPayload } = useContext(AdditionalApiPropsContext);
  const { linuxExclude, getPath, getValue, filterByParents } = useContext(RuleContext);
  const invalidBaseProps = invalidHwProps({ cls, hw_type, subsystem: subsys });
  const [showModal, contextHolder] = usePreviewModal();

  const handlePreviewItem = (exp: string, subsystem: string) => () => showModal({
    subsystem,
    hw_type,
    parent: true,
    strictAcl: false,
    additionalApiParams: preview?.parents?.item,
    data: {
      class: cls,
      hw_type,
      subsystem,
      regex: [exp],
      linuxExclude,
      ...additionalPayload
    },
  });

  const { readonly } = GLOB.userInfo;
  const handleRegexChange = (subsystem?: string) => () => {
    if (!subsystem)
      return;

    filterByParents();
  };

  const handleRemoveRegex = (remove: (index: number | number[]) => void, index: number) => () => {
    remove(index);
    filterByParents();
  };

  return <>
    <Form.List name={getPath(['parents', 'regex'])} >
      {(regexItems, { remove, add }) => (
        <>
          <div className="xm-rule-filter-regex">
            {regexItems.map(({ key, name }) => {
              const regex = getValue<ParentRegex>(['parents', 'regex', name]);
              return <span className="xm-regex-item xm-parent" key={`parent-regex-list-${key}`}>
                <Input.Group
                  compact
                  className={invalidBaseProps || regex.regex.length < 2 || !regex.subsystem ? 'xm-disabled-input' : 'xm-enable-input'}
                >
                  <Form.Item name={[name, 'regex']} help={<></>} rules={[{ required: true }, validatePattern]}>
                    <Input
                      size="small"
                      disabled={!regex.subsystem}
                      onChange={handleRegexChange(regex.subsystem)}
                      addonBefore={
                        <Form.Item name={[name, 'subsystem']} help={<></>} rules={[{ required: true }]}>
                          <XmSelect
                            placeholder='subsystem'
                            size='small'
                            options={parentTypes}
                            onChange={filterByParents}
                            className={regex.subsystem && 'xm-subsystem-selected'}
                            ignoreReadonly
                          />
                        </Form.Item>
                      }
                    />
                  </Form.Item>
                  <Button
                    disabled={!regex.subsystem || !isPatternValid(regex.regex)}
                    size="small"
                    onClick={!(invalidBaseProps || regex.regex.length < 2 || !regex.subsystem) ? handlePreviewItem(regex.regex, regex.subsystem) : undefined}
                    icon={<EyeOutlined />}
                  />
                </Input.Group>
                <Button
                  size="small"
                  className={readonly || invalidBaseProps || getValue<ParentRegex[]>(['parents', 'regex'])?.length < 2 ? 'xm-disabled-delete' : 'hover-danger'}
                  disabled={readonly || invalidBaseProps || getValue<ParentRegex[]>(['parents', 'regex'])?.length < 2}
                  type='link'
                  title='Remove rule'
                  icon={<DeleteOutlined onClick={handleRemoveRegex(remove, name)} />} />
              </span>;
            })}
          </div>
          <AddButton disabled={!getValue<ParentRegex[]>(['parents', 'regex'])} onClick={() => add({ subsystem: undefined, regex: '.*' })} />
        </>
      )}
    </Form.List>
    {contextHolder}
  </>;
}

export default RegexList;